// pages/EstadisticasReservas.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Select, Space, Typography, message, Tag,
  Tooltip, Row, Col, Badge, Card, DatePicker, Grid,
  FloatButton, Segmented, Empty, Progress, Avatar, List,
  Divider, Input
} from 'antd';
import {
  BarChartOutlined, PieChartOutlined, AreaChartOutlined,
  ReloadOutlined, FilterOutlined, SearchOutlined, DownloadOutlined,
  DollarOutlined, TeamOutlined, CalendarOutlined, GlobalOutlined,
  AppstoreOutlined, TrophyOutlined, FireOutlined,
  FundOutlined, ShopOutlined, TagsOutlined,
  ClearOutlined, RiseOutlined, HomeOutlined, ClockCircleOutlined,
  UserOutlined, PercentageOutlined, StopOutlined, CheckCircleOutlined,
  CloseCircleOutlined, CoffeeOutlined, SolutionOutlined, BankOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from '../utils/dayjsConfig';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ComposedChart, Treemap
} from 'recharts';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// 🎨 Paleta premium
const COLORS = [
  '#1890ff', '#52c41a', '#faad14', '#ff4d4f', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb',
  '#f5222d', '#a0522d', '#096dd9', '#c41d7f', '#5b8c00'
];

const REGIMEN_COLORS = {
  'PC': '#fa8c16',
  'MP': '#1890ff',
  'HD': '#52c41a',
  'AD': '#722ed1',
  'SA': '#eb2f96',
};

const getFlagEmoji = (isoCode) => {
  if (!isoCode || isoCode.length !== 2) return '🌍';
  try {
    const codePoints = isoCode.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  } catch { return '🌍'; }
};

const formatEuro = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0,00 €';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(value);
};

// ============ KPI CARD ============
const KPICard = ({ title, value, icon, color = '#1890ff', subtitle, isMobile }) => (
  <Card
    bordered={false}
    styles={{ body: { padding: isMobile ? 14 : 20 } }}
    style={{
      background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 12, height: '100%',
      position: 'relative', overflow: 'hidden',
    }}
    hoverable
  >
    <div style={{
      position: 'absolute', right: -20, top: -20, opacity: 0.08,
      fontSize: isMobile ? 80 : 120, color, pointerEvents: 'none',
    }}>
      {icon}
    </div>
    <Space direction="vertical" size={4} style={{ width: '100%', position: 'relative', zIndex: 1 }}>
      <Space size={6}>
        <span style={{ color, fontSize: isMobile ? 14 : 16 }}>{icon}</span>
        <Text type="secondary" style={{
          fontSize: isMobile ? 11 : 12, textTransform: 'uppercase',
          letterSpacing: 0.5, fontWeight: 600,
        }}>
          {title}
        </Text>
      </Space>
      <span style={{
        fontSize: isMobile ? 20 : 26, fontWeight: 700,
        color: '#1f1f1f', lineHeight: 1.1,
      }}>
        {value}
      </span>
      {subtitle && <Text type="secondary" style={{ fontSize: isMobile ? 11 : 12 }}>{subtitle}</Text>}
    </Space>
  </Card>
);

const EstadisticasReservas = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [reservas, setReservas] = useState([]);
  const [filtrosDrawerVisible, setFiltrosDrawerVisible] = useState(false);

  const [filtros, setFiltros] = useState({
    fechaDesde: dayjs('2025-01-01'),
    fechaHasta: dayjs('2027-12-31'),
    agencia: null,
    subagencia: null,
    regimen: [],
    tipofac: [],
    pais: null,
    texto: '',
    soloActivas: false,
  });

  const [agrupacion, setAgrupacion] = useState('agencia');
  const [agencias, setAgencias] = useState([]);

  // ============ CARGA INICIAL ============
  useEffect(() => {
    fetchAgencias();
    fetchReservas();
  }, []);

  const fetchAgencias = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/agencias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencias(res.data.data || []);
    } catch (e) { console.error('Error agencias:', e); }
  };

  const fetchReservas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}/api/estadisticas/reservas`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 🔥 Manejo flexible de la respuesta
      let raw = res.data?.data ?? res.data;
      if (!Array.isArray(raw)) raw = [];

      console.log('📦 Reservas recibidas:', raw.length, raw[0]);

      // Normalizar cada fila: parsear números, fechas válidas
      const normalized = raw.map((d, idx) => ({
        ...d,
        _key: `${d.numreserva ?? 'x'}-${idx}`,
        numreserva: d.numreserva != null ? Number(d.numreserva) : null,
        Numocupante: d.Numocupante != null ? Number(d.Numocupante) : null,
        importebase: d.importebase != null ? parseFloat(d.importebase) : 0,
        importetotal: d.importetotal != null ? parseFloat(d.importetotal) : 0,
        anulada: d.anulada === true || d.anulada === 'true' || d.anulada === 1,
        fechareserva: d.fechareserva || null,
        fechabooking: d.fechabooking || null,
        fecha: d.fecha || null,
        agencia: d.agencia || 'SIN',
        nombreagencia: d.nombreagencia || '',
        subagen: d.subagen || '',
        nombresubagencia: d.nombresubagencia || '',
        regimen: d.regimen || 'SIN',
        tipofac: d.tipofac || 'SIN',
        descripciontipofacturacion: d.descripciontipofacturacion || '',
        tipohab: d.tipohab || 'SIN',
        descripcionhabitacionventa2: d.descripcionhabitacionventa2 || d.tipohab || '',
        PaisISO: d.PaisISO || '',
        Pais: d.Pais || 'SIN PAÍS',
      }));

      setReservas(normalized);
    } catch (error) {
      console.error('❌ Error estadísticas reservas:', error);
      message.error('Error al cargar las estadísticas');
      setReservas([]);
    } finally {
      setLoading(false);
    }
  };

  // ============ 🎯 FILTRADO EN CLIENTE (afecta a TODO) ============
  const datosFiltrados = useMemo(() => {
    return reservas.filter(d => {
      // Fecha reserva (si existe)
      if (filtros.fechaDesde && d.fechareserva) {
        if (dayjs(d.fechareserva).isBefore(dayjs(filtros.fechaDesde).startOf('day'))) return false;
      }
      if (filtros.fechaHasta && d.fechareserva) {
        if (dayjs(d.fechareserva).isAfter(dayjs(filtros.fechaHasta).endOf('day'))) return false;
      }

      if (filtros.agencia && d.agencia !== filtros.agencia) return false;
      if (filtros.subagencia && d.subagen !== filtros.subagencia) return false;
      if (filtros.regimen?.length && !filtros.regimen.includes(d.regimen)) return false;
      if (filtros.tipofac?.length && !filtros.tipofac.includes(d.tipofac)) return false;
      if (filtros.pais && d.PaisISO !== filtros.pais) return false;
      if (filtros.soloActivas && d.anulada) return false;

      if (filtros.texto) {
        const t = filtros.texto.toLowerCase();
        const hay = (
          (d.nombreagencia || '').toLowerCase().includes(t) ||
          (d.nombresubagencia || '').toLowerCase().includes(t) ||
          (d.Pais || '').toLowerCase().includes(t) ||
          String(d.numreserva ?? '').includes(t) ||
          (d.descripcionhabitacionventa2 || '').toLowerCase().includes(t)
        );
        if (!hay) return false;
      }
      return true;
    });
  }, [reservas, filtros]);

  const filtrosActivos = useMemo(() => {
    let n = 0;
    if (filtros.agencia) n++;
    if (filtros.subagencia) n++;
    if (filtros.regimen?.length) n++;
    if (filtros.tipofac?.length) n++;
    if (filtros.pais) n++;
    if (filtros.texto) n++;
    if (filtros.soloActivas) n++;
    return n;
  }, [filtros]);

  // Listas únicas para filtros
  const subagenciasDisponibles = useMemo(() => {
    const s = new Set();
    reservas.forEach(d => { if (d.subagen) s.add(d.subagen); });
    return Array.from(s).sort();
  }, [reservas]);

  const regimenesDisponibles = useMemo(() => {
    const s = new Set();
    reservas.forEach(d => { if (d.regimen) s.add(d.regimen); });
    return Array.from(s).sort();
  }, [reservas]);

  const tiposFacDisponibles = useMemo(() => {
    const s = new Set();
    reservas.forEach(d => { if (d.tipofac) s.add(d.tipofac); });
    return Array.from(s).sort();
  }, [reservas]);

  const paisesDisponibles = useMemo(() => {
    const map = new Map();
    reservas.forEach(d => {
      if (d.PaisISO && d.Pais) map.set(d.PaisISO, d.Pais);
    });
    return Array.from(map.entries())
      .map(([iso, nombre]) => ({ iso, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [reservas]);

  // ============ 📊 KPIs ============
  const kpis = useMemo(() => {
    const activas = datosFiltrados.filter(d => !d.anulada);

    const reservasUnicas = new Set(datosFiltrados.map(d => d.numreserva).filter(Boolean));
    const reservasActivas = new Set(activas.map(d => d.numreserva).filter(Boolean));
    const reservasCanceladas = new Set(
      datosFiltrados.filter(d => d.anulada).map(d => d.numreserva).filter(Boolean)
    );

    const ingresos = activas.reduce((s, d) => s + (parseFloat(d.importetotal) || 0), 0);
    const ingresosBase = activas.reduce((s, d) => s + (parseFloat(d.importebase) || 0), 0);

    // Noches: filas con fecha real (noche) no anuladas
    const filasConNoche = activas.filter(d => d.fecha);
    const nochesVendidas = filasConNoche.length;

    const adr = nochesVendidas > 0 ? ingresos / nochesVendidas : 0;

    const paxUnicos = new Set(
      activas.map(d => `${d.numreserva}-${d.Numocupante}`)
    ).size;

    // Estancia media: noches únicas por reserva
    const nochesPorReserva = {};
    filasConNoche.forEach(d => {
      if (!nochesPorReserva[d.numreserva]) nochesPorReserva[d.numreserva] = new Set();
      nochesPorReserva[d.numreserva].add(dayjs(d.fecha).format('YYYY-MM-DD'));
    });
    const estancias = Object.values(nochesPorReserva).map(set => set.size);
    const estanciaMedia = estancias.length > 0
      ? estancias.reduce((s, n) => s + n, 0) / estancias.length
      : 0;

    const tasaCancelacion = reservasUnicas.size > 0
      ? (reservasCanceladas.size / reservasUnicas.size) * 100
      : 0;

    const ticketMedio = reservasActivas.size > 0 ? ingresos / reservasActivas.size : 0;

    const leadTimes = activas
      .filter(d => d.fechabooking && d.fechareserva)
      .map(d => dayjs(d.fechareserva).diff(dayjs(d.fechabooking), 'days'));
    const leadTimeMedio = leadTimes.length > 0
      ? leadTimes.reduce((s, l) => s + l, 0) / leadTimes.length
      : 0;

    return {
      totalReservas: reservasUnicas.size,
      reservasActivas: reservasActivas.size,
      reservasCanceladas: reservasCanceladas.size,
      ingresos,
      ingresosBase,
      nochesVendidas,
      adr,
      paxUnicos,
      estanciaMedia,
      tasaCancelacion,
      ticketMedio,
      leadTimeMedio,
      margen: ingresos - ingresosBase,
    };
  }, [datosFiltrados]);

  // ============ 📈 GRÁFICOS ============
  const chartData = useMemo(() => {
    const filtered = datosFiltrados.filter(d => !d.anulada);

    // Por agencia
    const agMap = {};
    filtered.forEach(d => {
      if (!agMap[d.agencia]) agMap[d.agencia] = {
        name: d.agencia, fullName: d.nombreagencia,
        agencia: d.agencia, ingresos: 0, noches: 0, reservasSet: new Set(),
      };
      agMap[d.agencia].ingresos += parseFloat(d.importetotal) || 0;
      agMap[d.agencia].noches += 1;
      agMap[d.agencia].reservasSet.add(d.numreserva);
    });
    const porAgencia = Object.values(agMap).map(a => ({
      ...a,
      reservas: a.reservasSet.size,
      adr: a.noches > 0 ? a.ingresos / a.noches : 0,
    })).sort((a, b) => b.ingresos - a.ingresos);

    // Por régimen
    const regMap = {};
    filtered.forEach(d => {
      const k = d.regimen || 'SIN';
      if (!regMap[k]) regMap[k] = {
        name: k, regimen: k, ingresos: 0, noches: 0, reservasSet: new Set(),
      };
      regMap[k].ingresos += parseFloat(d.importetotal) || 0;
      regMap[k].noches += 1;
      regMap[k].reservasSet.add(d.numreserva);
    });
    const porRegimen = Object.values(regMap).map(r => ({
      ...r, reservas: r.reservasSet.size,
    })).sort((a, b) => b.ingresos - a.ingresos);

    // Por país
    const paisMap = {};
    filtered.filter(d => d.PaisISO).forEach(d => {
      if (!paisMap[d.PaisISO]) paisMap[d.PaisISO] = {
        iso: d.PaisISO, name: d.Pais, ingresos: 0, noches: 0, pax: 0,
      };
      paisMap[d.PaisISO].ingresos += parseFloat(d.importetotal) || 0;
      paisMap[d.PaisISO].noches += 1;
      paisMap[d.PaisISO].pax += 1;
    });
    const porPais = Object.values(paisMap).sort((a, b) => b.ingresos - a.ingresos);

    // Por tipo habitación
    const habMap = {};
    filtered.forEach(d => {
      const k = d.tipohab || 'SIN';
      if (!habMap[k]) habMap[k] = {
        name: d.descripcionhabitacionventa2 || k, tipo: k, ingresos: 0, noches: 0,
      };
      habMap[k].ingresos += parseFloat(d.importetotal) || 0;
      habMap[k].noches += 1;
    });
    const porHabitacion = Object.values(habMap).sort((a, b) => b.ingresos - a.ingresos);

    // Por tipo facturación
    const tfMap = {};
    filtered.forEach(d => {
      const k = d.tipofac || 'SIN';
      if (!tfMap[k]) tfMap[k] = {
        name: `${k} - ${d.descripciontipofacturacion || ''}`,
        tipo: k, ingresos: 0, noches: 0,
      };
      tfMap[k].ingresos += parseFloat(d.importetotal) || 0;
      tfMap[k].noches += 1;
    });
    const porTipoFac = Object.values(tfMap).sort((a, b) => b.ingresos - a.ingresos);

    // Evolución por fecha de reserva
    const fecMap = {};
    filtered.forEach(d => {
      if (!d.fechareserva) return;
      const key = dayjs(d.fechareserva).format('YYYY-MM-DD');
      if (!fecMap[key]) fecMap[key] = {
        fecha: key, ingresos: 0, noches: 0, reservasSet: new Set(),
      };
      fecMap[key].ingresos += parseFloat(d.importetotal) || 0;
      fecMap[key].noches += 1;
      fecMap[key].reservasSet.add(d.numreserva);
    });
    const porFecha = Object.values(fecMap).map(f => ({
      ...f, reservas: f.reservasSet.size,
    })).sort((a, b) => a.fecha.localeCompare(b.fecha));

    // Ocupación por noche
    const nocheMap = {};
    filtered.filter(d => d.fecha).forEach(d => {
      const key = dayjs(d.fecha).format('YYYY-MM-DD');
      if (!nocheMap[key]) nocheMap[key] = {
        fecha: key, habitacionesSet: new Set(), pax: 0, ingresos: 0,
      };
      nocheMap[key].habitacionesSet.add(`${d.numreserva}-${d.Numocupante}`);
      nocheMap[key].pax += 1;
      nocheMap[key].ingresos += parseFloat(d.importetotal) || 0;
    });
    const porNoche = Object.values(nocheMap).map(n => ({
      fecha: n.fecha,
      habitaciones: n.habitacionesSet.size,
      pax: n.pax,
      ingresos: n.ingresos,
    })).sort((a, b) => a.fecha.localeCompare(b.fecha));

    // Subagencias
    const subMap = {};
    filtered.filter(d => d.subagen).forEach(d => {
      const k = d.subagen;
      if (!subMap[k]) subMap[k] = {
        name: d.nombresubagencia || d.subagen, subagencia: d.subagen,
        ingresos: 0, noches: 0, reservasSet: new Set(),
      };
      subMap[k].ingresos += parseFloat(d.importetotal) || 0;
      subMap[k].noches += 1;
      subMap[k].reservasSet.add(d.numreserva);
    });
    const porSubagencia = Object.values(subMap).map(s => ({
      ...s, reservas: s.reservasSet.size,
    })).sort((a, b) => b.ingresos - a.ingresos);

    return { porAgencia, porRegimen, porPais, porHabitacion, porTipoFac, porFecha, porNoche, porSubagencia };
  }, [datosFiltrados]);

  // ============ TABLA AGRUPADA ============
  const tableData = useMemo(() => {
    const filtered = datosFiltrados.filter(d => !d.anulada);
    const map = {};

    filtered.forEach(d => {
      let key, label;
      switch (agrupacion) {
        case 'agencia':
          key = d.agencia;
          label = `${d.agencia} - ${d.nombreagencia || ''}`;
          break;
        case 'subagencia':
          key = d.subagen || 'SIN_SUB';
          label = d.nombresubagencia || d.subagen || 'Sin Subagencia';
          break;
        case 'regimen':
          key = d.regimen;
          label = `Régimen ${d.regimen}`;
          break;
        case 'pais':
          key = d.PaisISO || 'SIN';
          label = `${getFlagEmoji(d.PaisISO)} ${d.Pais || 'Sin País'}`;
          break;
        case 'tipohab':
          key = d.tipohab;
          label = d.descripcionhabitacionventa2 || d.tipohab;
          break;
        case 'tipofac':
          key = d.tipofac;
          label = `${d.tipofac} - ${d.descripciontipofacturacion || ''}`;
          break;
        default:
          key = d.agencia;
          label = d.agencia;
      }

      if (!map[key]) {
        map[key] = {
          key, label,
          agencia: d.agencia, nombreagencia: d.nombreagencia,
          subagen: d.subagen, nombresubagencia: d.nombresubagencia,
          regimen: d.regimen, PaisISO: d.PaisISO, Pais: d.Pais,
          tipohab: d.tipohab, descripcionhabitacionventa2: d.descripcionhabitacionventa2,
          tipofac: d.tipofac, descripciontipofacturacion: d.descripciontipofacturacion,
          ingresos: 0, noches: 0,
          reservasSet: new Set(), paxSet: new Set(),
        };
      }
      map[key].ingresos += parseFloat(d.importetotal) || 0;
      map[key].noches += 1;
      map[key].reservasSet.add(d.numreserva);
      map[key].paxSet.add(`${d.numreserva}-${d.Numocupante}`);
    });

    const arr = Object.values(map).map(r => ({
      ...r,
      reservas: r.reservasSet.size,
      pax: r.paxSet.size,
      adr: r.noches > 0 ? r.ingresos / r.noches : 0,
    })).sort((a, b) => b.ingresos - a.ingresos);

    const grandTotal = arr.reduce((s, r) => s + r.ingresos, 0);
    return arr.map(r => ({
      ...r,
      porcentaje: grandTotal > 0 ? (r.ingresos / grandTotal) * 100 : 0,
    }));
  }, [datosFiltrados, agrupacion]);

  // ============ COLUMNAS TABLA ============
  const tableColumns = useMemo(() => {
    const firstColMap = {
      agencia: {
        title: 'Agencia', width: isMobile ? 160 : 280,
        render: (v, r) => (
          <Space>
            <Avatar size="small" style={{ backgroundColor: COLORS[0] }}>
              {r.agencia?.substring(0, 2)}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>{r.agencia}</Text>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.nombreagencia}</div>
            </div>
          </Space>
        ),
      },
      subagencia: {
        title: 'Subagencia', width: isMobile ? 160 : 260,
        render: (v, r) => (
          <div>
            <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>{r.subagen || '—'}</Text>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.nombresubagencia}</div>
          </div>
        ),
      },
      regimen: {
        title: 'Régimen', width: 160,
        render: (v, r) => (
          <Tag color={REGIMEN_COLORS[r.regimen] || 'default'}
            style={{ fontWeight: 600, fontSize: 13, padding: '4px 10px' }}>
            {r.regimen}
          </Tag>
        ),
      },
      pais: {
        title: 'País', width: 200,
        render: (v, r) => (
          <Space>
            <span style={{ fontSize: 18 }}>{getFlagEmoji(r.PaisISO)}</span>
            <div>
              <Text strong style={{ fontSize: 13 }}>{r.PaisISO}</Text>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.Pais}</div>
            </div>
          </Space>
        ),
      },
      tipohab: {
        title: 'Tipo Habitación', width: 200,
        render: (v, r) => (
          <Space>
            <HomeOutlined style={{ color: '#722ed1' }} />
            <div>
              <Text strong style={{ fontSize: 13 }}>{r.tipohab}</Text>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.descripcionhabitacionventa2}</div>
            </div>
          </Space>
        ),
      },
      tipofac: {
        title: 'Tipo Facturación', width: 200,
        render: (v, r) => (
          <div>
            <Tag color="purple" style={{ fontWeight: 600 }}>{r.tipofac}</Tag>
            <Text style={{ fontSize: 12 }}>{r.descripciontipofacturacion}</Text>
          </div>
        ),
      },
    };

    const first = firstColMap[agrupacion];
    const cols = [{
      title: first.title, dataIndex: 'label', key: 'label',
      fixed: isMobile ? false : 'left', width: first.width,
      render: first.render,
    }];

    cols.push(
      {
        title: 'Ingresos', dataIndex: 'ingresos', key: 'ingresos',
        align: 'right', width: isMobile ? 110 : 140,
        sorter: (a, b) => a.ingresos - b.ingresos,
        defaultSortOrder: 'descend',
        render: (v) => (
          <Text strong style={{
            color: v < 0 ? '#ff4d4f' : '#52c41a',
            fontSize: isMobile ? 12 : 14,
          }}>
            {formatEuro(v)}
          </Text>
        ),
      },
      {
        title: 'Reservas', dataIndex: 'reservas', key: 'reservas',
        align: 'right', width: isMobile ? 80 : 100,
        sorter: (a, b) => a.reservas - b.reservas,
        render: (v) => <Badge count={v} style={{ backgroundColor: '#1890ff' }} />,
      },
      {
        title: 'Noches', dataIndex: 'noches', key: 'noches',
        align: 'right', width: 90,
        sorter: (a, b) => a.noches - b.noches,
        render: (v) => <Text>{formatNumber(v)}</Text>,
      },
      {
        title: 'Pax', dataIndex: 'pax', key: 'pax',
        align: 'right', width: 80, responsive: ['md'],
        render: (v) => <Text>{formatNumber(v)}</Text>,
      },
      {
        title: 'ADR', dataIndex: 'adr', key: 'adr',
        align: 'right', width: isMobile ? 100 : 120,
        sorter: (a, b) => a.adr - b.adr,
        render: (v) => (
          <Tooltip title="Average Daily Rate">
            <Text type="secondary">{formatEuro(v)}</Text>
          </Tooltip>
        ),
      },
      {
        title: '% Total', dataIndex: 'porcentaje', key: 'porcentaje',
        width: isMobile ? 90 : 160,
        sorter: (a, b) => a.porcentaje - b.porcentaje,
        render: (v) => (
          <Tooltip title={`${v.toFixed(2)}% del total`}>
            <Progress
              percent={Math.min(Math.abs(v), 100)}
              size="small"
              strokeColor={v < 0 ? '#ff4d4f' : '#1890ff'}
              format={() => `${v.toFixed(1)}%`}
            />
          </Tooltip>
        ),
      }
    );

    return cols;
  }, [agrupacion, isMobile]);

  // ============ PANEL DE FILTROS ============
  const filtrosBar = (
    <Card
      size="small"
      style={{
        marginBottom: 16, borderRadius: 12,
        background: 'linear-gradient(135deg, #f0f5ff 0%, #ffffff 100%)',
        border: '1px solid #e6f0ff',
        boxShadow: '0 2px 8px rgba(24,144,255,0.06)',
      }}
      styles={{ body: { padding: isMobile ? 12 : 16 } }}
    >
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={8} lg={5}>
          <Input
            placeholder="Buscar reserva, agencia, país..."
            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
            value={filtros.texto}
            onChange={(e) => setFiltros({ ...filtros, texto: e.target.value })}
            allowClear
          />
        </Col>

        <Col xs={24} sm={12} md={8} lg={5}>
          <RangePicker
            style={{ width: '100%' }}
            value={[filtros.fechaDesde, filtros.fechaHasta]}
            onChange={(dates) => setFiltros({
              ...filtros,
              fechaDesde: dates?.[0] || null,
              fechaHasta: dates?.[1] || null,
            })}
            format="DD/MM/YYYY"
            allowClear={false}
          />
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }} placeholder="Agencia"
            value={filtros.agencia}
            onChange={(v) => setFiltros({ ...filtros, agencia: v })}
            allowClear showSearch
            filterOption={(input, option) =>
              String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {agencias.map(a => (
              <Select.Option key={a.codigo} value={a.codigo}>
                {a.codigo} - {a.nombre}
              </Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }} placeholder="Subagencia"
            value={filtros.subagencia}
            onChange={(v) => setFiltros({ ...filtros, subagencia: v })}
            allowClear showSearch
          >
            {subagenciasDisponibles.map(s => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={2}>
          <Select
            style={{ width: '100%' }} mode="multiple"
            placeholder="Rég." maxTagCount={0}
            value={filtros.regimen}
            onChange={(v) => setFiltros({ ...filtros, regimen: v })}
            allowClear
          >
            {regimenesDisponibles.map(r => (
              <Select.Option key={r} value={r}>{r}</Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={2}>
          <Select
            style={{ width: '100%' }} mode="multiple"
            placeholder="T.Fac" maxTagCount={0}
            value={filtros.tipofac}
            onChange={(v) => setFiltros({ ...filtros, tipofac: v })}
            allowClear
          >
            {tiposFacDisponibles.map(t => (
              <Select.Option key={t} value={t}>{t}</Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Select
            style={{ width: '100%' }} placeholder="País"
            value={filtros.pais}
            onChange={(v) => setFiltros({ ...filtros, pais: v })}
            allowClear showSearch
            optionFilterProp="label"
          >
            {paisesDisponibles.map(p => (
              <Select.Option key={p.iso} value={p.iso} label={`${p.iso} - ${p.nombre}`}>
                {getFlagEmoji(p.iso)} {p.iso} - {p.nombre}
              </Select.Option>
            ))}
          </Select>
        </Col>

        <Col xs={12} sm={6} md={4} lg={3}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Tooltip title="Solo reservas activas">
              <Button
                type={filtros.soloActivas ? 'primary' : 'default'}
                icon={<CheckCircleOutlined />}
                onClick={() => setFiltros({ ...filtros, soloActivas: !filtros.soloActivas })}
              />
            </Tooltip>
            <Tooltip title="Recargar">
              <Button icon={<ReloadOutlined />} onClick={fetchReservas} loading={loading} />
            </Tooltip>
            <Tooltip title="Limpiar filtros">
              <Button
                icon={<ClearOutlined />}
                onClick={() => setFiltros({
                  fechaDesde: dayjs('2025-01-01'),
                  fechaHasta: dayjs('2027-12-31'),
                  agencia: null, subagencia: null,
                  regimen: [], tipofac: [], pais: null,
                  texto: '', soloActivas: false,
                })}
                disabled={filtrosActivos === 0}
              />
            </Tooltip>
          </Space>
        </Col>
      </Row>

      <Divider style={{ margin: '12px 0 10px' }} />

      <Row gutter={[12, 10]} align="middle" justify="space-between">
        <Col xs={24} md={14}>
          <Space wrap size={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <FilterOutlined /> Mostrando:
            </Text>
            <Tag color="blue" style={{ fontWeight: 600 }}>
              {kpis.totalReservas} reservas
            </Tag>
            <Tag color="green" style={{ fontWeight: 600 }}>
              {formatEuro(kpis.ingresos)}
            </Tag>
            <Tag color="cyan" style={{ fontWeight: 600 }}>
              {kpis.nochesVendidas} noches
            </Tag>
            {filtrosActivos > 0 && (
              <Tag color="orange" style={{ fontWeight: 600 }}>
                {filtrosActivos} filtro{filtrosActivos > 1 ? 's' : ''} activo{filtrosActivos > 1 ? 's' : ''}
              </Tag>
            )}
          </Space>
        </Col>
        <Col xs={24} md={10}>
          <Space wrap style={{ width: '100%', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Agrupar por:</Text>
            <Segmented
              size="small"
              value={agrupacion}
              onChange={setAgrupacion}
              options={[
                { label: 'Agencia', value: 'agencia', icon: <ShopOutlined /> },
                { label: 'Subag.', value: 'subagencia', icon: <BankOutlined /> },
                { label: 'Régimen', value: 'regimen', icon: <CoffeeOutlined /> },
                { label: 'País', value: 'pais', icon: <GlobalOutlined /> },
                { label: 'Hab.', value: 'tipohab', icon: <HomeOutlined /> },
                { label: 'T.Fac', value: 'tipofac', icon: <TagsOutlined /> },
              ]}
            />
          </Space>
        </Col>
      </Row>
    </Card>
  );

  // ============ RENDER ============
  return (
    <PageContainer
      title="Estadísticas de Reservas"
      subtitle={isMobile
        ? `${kpis.totalReservas} reservas`
        : `${kpis.totalReservas} reservas · ${formatEuro(kpis.ingresos)}`}
      icon={<HomeOutlined />}
      loading={loading}
      onRefresh={fetchReservas}
      badgeCount={kpis.totalReservas}
      badgeStatus="processing"
      extra={!isMobile && (
        <Button icon={<DownloadOutlined />} onClick={() => message.info('Exportando...')}>
          Exportar
        </Button>
      )}
      cardStyle={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
    >
      {/* FILTROS ARRIBA */}
      {filtrosBar}

      {/* KPIs PRINCIPALES */}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Reservas"
            value={formatNumber(kpis.totalReservas)}
            icon={<SolutionOutlined />}
            color="#1890ff"
            subtitle={`${kpis.reservasActivas} activas · ${kpis.reservasCanceladas} canceladas`}
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Ingresos"
            value={formatEuro(kpis.ingresos)}
            icon={<DollarOutlined />}
            color="#52c41a"
            subtitle={`Margen: ${formatEuro(kpis.margen)}`}
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Noches Vendidas"
            value={formatNumber(kpis.nochesVendidas)}
            icon={<CalendarOutlined />}
            color="#722ed1"
            subtitle={`Estancia media: ${formatNumber(kpis.estanciaMedia, 1)} noches`}
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="ADR"
            value={formatEuro(kpis.adr)}
            icon={<PercentageOutlined />}
            color="#fa8c16"
            subtitle="Average Daily Rate"
            isMobile={isMobile}
          />
        </Col>
      </Row>

      {/* KPIs SECUNDARIOS */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Pax Únicos"
            value={formatNumber(kpis.paxUnicos)}
            icon={<UserOutlined />}
            color="#13c2c2"
            subtitle="Ocupantes totales"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Ticket Medio"
            value={formatEuro(kpis.ticketMedio)}
            icon={<TrophyOutlined />}
            color="#eb2f96"
            subtitle="Por reserva"
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Tasa Cancelación"
            value={`${formatNumber(kpis.tasaCancelacion, 1)}%`}
            icon={<StopOutlined />}
            color="#ff4d4f"
            subtitle={`${kpis.reservasCanceladas} canceladas`}
            isMobile={isMobile}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <KPICard
            title="Lead Time"
            value={`${formatNumber(kpis.leadTimeMedio, 1)} días`}
            icon={<ClockCircleOutlined />}
            color="#faad14"
            subtitle="Antelación media"
            isMobile={isMobile}
          />
        </Col>
      </Row>

      {/* TOP CARDS */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #faad14' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#faad14' }} icon={<TrophyOutlined />} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Top Agencia</Text>
                <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {chartData.porAgencia[0]?.agencia} - {chartData.porAgencia[0]?.fullName}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(chartData.porAgencia[0]?.ingresos)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #13c2c2' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#13c2c2', fontSize: 20 }}>
                {getFlagEmoji(chartData.porPais[0]?.iso)}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Top País</Text>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {chartData.porPais[0]?.name || '—'}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(chartData.porPais[0]?.ingresos)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #eb2f96' }}
            styles={{ body: { padding: 14 } }}>
            <Space>
              <Avatar style={{ backgroundColor: '#eb2f96' }} icon={<FireOutlined />} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>Top Régimen</Text>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {chartData.porRegimen[0]?.regimen || '—'}
                </div>
                <Text strong style={{ color: '#52c41a' }}>{formatEuro(chartData.porRegimen[0]?.ingresos)}</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* EVOLUCIÓN TEMPORAL */}
      <Card
        title={
          <Space>
            <AreaChartOutlined style={{ color: '#1890ff' }} />
            <span>Evolución de Reservas</span>
            <Tag color="blue">{chartData.porFecha.length} días</Tag>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 12 }}
        size="small"
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porFecha.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
            <ComposedChart data={chartData.porFecha}>
              <defs>
                <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#52c41a" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }}
                tickFormatter={(v) => dayjs(v).format('DD/MM')} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelFormatter={(v) => dayjs(v).format('DD MMMM YYYY')}
                formatter={(value, name) => {
                  if (name === 'Ingresos') return [formatEuro(value), 'Ingresos'];
                  return [formatNumber(value), name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area yAxisId="left" type="monotone" dataKey="ingresos" name="Ingresos"
                stroke="#52c41a" strokeWidth={2} fill="url(#colorIngresos)" />
              <Bar yAxisId="right" dataKey="reservas" name="Reservas"
                fill="#1890ff" radius={[4, 4, 0, 0]} barSize={16} opacity={0.8} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty description="Sin datos temporales" />}
      </Card>

      {/* OCUPACIÓN POR NOCHE */}
      <Card
        title={
          <Space>
            <CalendarOutlined style={{ color: '#722ed1' }} />
            <span>Ocupación por Noche</span>
            <Tag color="purple">{chartData.porNoche.length} noches</Tag>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 12 }}
        size="small"
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porNoche.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 240 : 300}>
            <ComposedChart data={chartData.porNoche}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }}
                tickFormatter={(v) => dayjs(v).format('DD/MM')} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                labelFormatter={(v) => dayjs(v).format('DD MMMM YYYY')}
                formatter={(value, name) => {
                  if (name === 'Ingresos') return [formatEuro(value), 'Ingresos'];
                  if (name === 'Habitaciones') return [formatNumber(value), 'Habitaciones'];
                  return [formatNumber(value), 'Pax'];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="left" dataKey="habitaciones" name="Habitaciones"
                stackId="a" fill="#722ed1" opacity={0.85} />
              <Bar yAxisId="left" dataKey="pax" name="Pax"
                stackId="a" fill="#1890ff" radius={[4, 4, 0, 0]} opacity={0.85} />
              <Line yAxisId="right" type="monotone" dataKey="ingresos" name="Ingresos"
                stroke="#52c41a" strokeWidth={2.5} dot={{ r: 3, fill: '#52c41a' }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>

      {/* TOP AGENCIAS + PIE RÉGIMEN */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          <Card
            title={<Space><BarChartOutlined style={{ color: '#1890ff' }} /> Top Agencias por Ingresos</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porAgencia.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <BarChart data={chartData.porAgencia.slice(0, 10)} layout="vertical"
                  margin={{ left: isMobile ? 5 : 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={isMobile ? 45 : 60} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(value, name, props) => [formatEuro(value), props.payload.fullName || props.payload.name]}
                  />
                  <Bar dataKey="ingresos" radius={[0, 6, 6, 0]}>
                    {chartData.porAgencia.slice(0, 10).map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card
            title={<Space><PieChartOutlined style={{ color: '#eb2f96' }} /> Mix de Regímenes</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porRegimen.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 350}>
                <PieChart>
                  <Pie
                    data={chartData.porRegimen}
                    dataKey="ingresos"
                    nameKey="regimen"
                    cx="50%" cy="50%"
                    innerRadius={isMobile ? 40 : 60}
                    outerRadius={isMobile ? 80 : 110}
                    paddingAngle={4}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                    labelLine={{ stroke: '#d9d9d9' }}
                  >
                    {chartData.porRegimen.map((entry, i) => (
                      <Cell key={i} fill={REGIMEN_COLORS[entry.regimen] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(v) => formatEuro(v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>
      </Row>

      {/* TOP PAÍSES + TIPOS HABITACIÓN */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><GlobalOutlined style={{ color: '#13c2c2' }} /> Top Países por Ingresos</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porPais.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                <BarChart data={chartData.porPais.slice(0, 10)} layout="vertical"
                  margin={{ left: isMobile ? 5 : 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis dataKey="iso" type="category" tick={{ fontSize: 13 }}
                    width={isMobile ? 50 : 60}
                    tickFormatter={(v) => `${getFlagEmoji(v)} ${v}`} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(value, name, props) => [
                      formatEuro(value),
                      `${props.payload.name} (${props.payload.noches} noches)`
                    ]}
                  />
                  <Bar dataKey="ingresos" radius={[0, 6, 6, 0]}>
                    {chartData.porPais.slice(0, 10).map((entry, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={<Space><HomeOutlined style={{ color: '#722ed1' }} /> Tipos de Habitación</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: isMobile ? 8 : 16 } }}
          >
            {chartData.porHabitacion.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 380}>
                <BarChart data={chartData.porHabitacion.slice(0, 10)} layout="vertical"
                  margin={{ left: isMobile ? 5 : 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis dataKey="tipo" type="category" tick={{ fontSize: 11 }}
                    width={isMobile ? 45 : 60} />
                  <RTooltip
                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    formatter={(value, name, props) => [
                      formatEuro(value),
                      `${props.payload.name} (${props.payload.noches} noches)`
                    ]}
                  />
                  <Bar dataKey="ingresos" radius={[0, 6, 6, 0]}>
                    {chartData.porHabitacion.slice(0, 10).map((entry, i) => (
                      <Cell key={i} fill={COLORS[(i + 5) % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty />}
          </Card>
        </Col>
      </Row>

      {/* RANKINGS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={<Space><TrophyOutlined style={{ color: '#faad14' }} /> Ranking Países</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: 0 } }}
          >
            {chartData.porPais.length > 0 ? (
              <List
                dataSource={chartData.porPais.slice(0, 8)}
                renderItem={(item, index) => {
                  const max = chartData.porPais[0]?.ingresos || 1;
                  const percent = (item.ingresos / max) * 100;
                  const medal = ['🥇', '🥈', '🥉'][index] || `#${index + 1}`;
                  return (
                    <List.Item style={{ padding: '10px 16px' }}>
                      <div style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <span style={{ fontSize: 16, width: 28, textAlign: 'center' }}>{medal}</span>
                            <span style={{ fontSize: 20 }}>{getFlagEmoji(item.iso)}</span>
                            <div>
                              <Text strong style={{ fontSize: 13 }}>{item.name}</Text>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                {item.noches} noches · {item.pax} pax
                              </div>
                            </div>
                          </Space>
                          <Text strong style={{ color: '#52c41a' }}>{formatEuro(item.ingresos)}</Text>
                        </Space>
                        <Progress percent={percent} showInfo={false}
                          strokeColor={COLORS[index % COLORS.length]} size="small"
                          style={{ marginTop: 6 }} />
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : <Empty />}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={<Space><BankOutlined style={{ color: '#eb2f96' }} /> Ranking Subagencias</Space>}
            size="small"
            style={{ borderRadius: 12 }}
            styles={{ body: { padding: 0 } }}
          >
            {chartData.porSubagencia.length > 0 ? (
              <List
                dataSource={chartData.porSubagencia.slice(0, 8)}
                renderItem={(item, index) => {
                  const max = chartData.porSubagencia[0]?.ingresos || 1;
                  const percent = (item.ingresos / max) * 100;
                  const medal = ['🥇', '🥈', '🥉'][index] || `#${index + 1}`;
                  return (
                    <List.Item style={{ padding: '10px 16px' }}>
                      <div style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                          <Space>
                            <span style={{ fontSize: 16, width: 28, textAlign: 'center' }}>{medal}</span>
                            <div>
                              <Text strong style={{ fontSize: 13 }}>{item.subagencia}</Text>
                              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{item.name}</div>
                            </div>
                          </Space>
                          <Text strong style={{ color: '#52c41a' }}>{formatEuro(item.ingresos)}</Text>
                        </Space>
                        <Progress percent={percent} showInfo={false}
                          strokeColor={COLORS[(index + 3) % COLORS.length]} size="small"
                          style={{ marginTop: 6 }} />
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : <Empty description="Sin subagencias" />}
          </Card>
        </Col>
      </Row>

      {/* TREEMAP PAÍSES */}
      <Card
        title={<Space><GlobalOutlined style={{ color: '#722ed1' }} /> Mapa de Países</Space>}
        size="small"
        style={{ borderRadius: 12, marginBottom: 16 }}
        styles={{ body: { padding: isMobile ? 8 : 16 } }}
      >
        {chartData.porPais.length > 0 ? (
          <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
            <Treemap
              data={chartData.porPais.map(c => ({ ...c, size: Math.abs(c.ingresos) }))}
              dataKey="size" aspectRatio={4 / 3} stroke="#fff" fill="#8884d8"
              content={({ x, y, width, height, value, index }) => {
                const color = COLORS[index % COLORS.length];
                const iso = chartData.porPais[index]?.iso;
                const name = chartData.porPais[index]?.name;
                return (
                  <g>
                    <rect x={x} y={y} width={width} height={height}
                      style={{ fill: color, stroke: '#fff', strokeWidth: 2, fillOpacity: 0.85 }} />
                    {width > 60 && height > 30 && (
                      <>
                        <text x={x + width / 2} y={y + height / 2 - 8}
                          textAnchor="middle" fill="#fff" fontSize={18}>
                          {getFlagEmoji(iso)}
                        </text>
                        <text x={x + width / 2} y={y + height / 2 + 10}
                          textAnchor="middle" fill="#fff" fontSize={11}>
                          {formatEuro(value)}
                        </text>
                      </>
                    )}
                  </g>
                );
              }}
            >
              <RTooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                formatter={(v, name, props) => [formatEuro(props.payload.ingresos), props.payload.name]}
              />
            </Treemap>
          </ResponsiveContainer>
        ) : <Empty />}
      </Card>

      {/* TABLA PRINCIPAL */}
      <Card
        title={
          <Space>
            <BarChartOutlined style={{ color: '#722ed1' }} />
            <span>
              Desglose por {
                agrupacion === 'agencia' ? 'Agencia'
                  : agrupacion === 'subagencia' ? 'Subagencia'
                    : agrupacion === 'regimen' ? 'Régimen'
                      : agrupacion === 'pais' ? 'País'
                        : agrupacion === 'tipohab' ? 'Tipo Habitación' : 'Tipo Facturación'
              }
            </span>
            <Badge count={tableData.length} style={{ backgroundColor: '#722ed1' }} />
          </Space>
        }
        size="small"
        style={{ borderRadius: 12 }}
        styles={{ body: { padding: isMobile ? 0 : 16 } }}
      >
        <Table
          columns={tableColumns}
          dataSource={tableData}
          rowKey="key"
          loading={loading}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: isMobile ? 500 : 1100 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: !isMobile,
            showTotal: (t) => `${t} registros`,
          }}
          locale={{ emptyText: <Empty description="No hay datos con los filtros aplicados" /> }}
          summary={(pageData) => {
            const ingresos = pageData.reduce((s, r) => s + r.ingresos, 0);
            const noches = pageData.reduce((s, r) => s + r.noches, 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 700 }}>
                  <Table.Summary.Cell index={0}><Text strong>TOTAL PÁGINA</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ color: '#52c41a' }}>{formatEuro(ingresos)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong>{formatNumber(noches)}</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} />
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>

      {isMobile && (
        <FloatButton
          icon={<FilterOutlined />}
          type="primary"
          style={{ right: 16, bottom: 80 }}
          onClick={() => setFiltrosDrawerVisible(true)}
          tooltip="Filtros"
        />
      )}
    </PageContainer>
  );
};

export default EstadisticasReservas;